import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import "../styles/PremiumPopup.css";
import { useLanguage } from "../i18n/LanguageContext";

const CROWN_IMG   = require("../assets/premium/crown_3x.png");
const DIAMOND_IMG = require("../assets/premium/diamond_3x.png");
const FAMILY_IMG  = require("../assets/premium/family_3x.png");

const PREMIUM_ICON_TYPES = ["infinity","folder","map","chat","shield"];
const PLUS_ICON_TYPES    = ["infinity","users","groups","map","star"];

function FeatureIcon({ type, isPlus }) {
  const cls = `premFeatureIcon${isPlus ? " plus" : ""}`;
  const paths = {
    infinity: <path d="M12 12c-2-2.5-4-4-6-4a4 4 0 0 0 0 8c2 0 4-1.5 6-4zm0 0c2 2.5 4 4 6 4a4 4 0 0 0 0-8c-2 0-4 1.5-6 4z" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" fill="none"/>,
    folder:   <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" stroke="#fff" strokeWidth="2" strokeLinejoin="round" fill="none"/>,
    map:      <><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" stroke="#fff" strokeWidth="2" fill="none"/><circle cx="12" cy="9" r="2.5" stroke="#fff" strokeWidth="2" fill="none"/></>,
    chat:     <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="#fff" strokeWidth="2" strokeLinejoin="round" fill="none"/>,
    shield:   <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="#fff" strokeWidth="2" strokeLinejoin="round" fill="none"/><path d="M9 12l2 2 4-4" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/></>,
    users:    <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="#fff" strokeWidth="2" strokeLinecap="round" fill="none"/><circle cx="9" cy="7" r="4" stroke="#fff" strokeWidth="2" fill="none"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" stroke="#fff" strokeWidth="2" strokeLinecap="round" fill="none"/></>,
    groups:   <><circle cx="9" cy="7" r="3" stroke="#fff" strokeWidth="2" fill="none"/><circle cx="17" cy="7" r="3" stroke="#fff" strokeWidth="2" fill="none"/><path d="M1 20v-1a7 7 0 0 1 7-7h.5" stroke="#fff" strokeWidth="2" strokeLinecap="round" fill="none"/><path d="M13 20v-1a7 7 0 0 1 7-7h.5" stroke="#fff" strokeWidth="2" strokeLinecap="round" fill="none"/></>,
    star:     <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" stroke="#fff" strokeWidth="2" strokeLinejoin="round" fill="none"/>,
  };
  return (
    <div className={cls}>
      <svg viewBox="0 0 24 24" width="20" height="20">{paths[type] || paths.star}</svg>
    </div>
  );
}

function CheckIcon() {
  return (
    <div className="premFeatureCheck">
      <svg viewBox="0 0 24 24" fill="none" width="15" height="15">
        <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </div>
  );
}

function GlowDots() {
  return (
    <div className="premGlowDots" aria-hidden="true">
      {[...Array(8)].map((_, i) => (
        <span key={i} className={`premGlowDot gd${i + 1}`} />
      ))}
    </div>
  );
}

function FamilyAura() {
  return (
    <div className="premFamilyAura" aria-hidden="true">
      <span className="faRing fa1" />
      <span className="faRing fa2" />
      <span className="faRing fa3" />
      <span className="faStar fs1">✦</span>
      <span className="faStar fs2">✦</span>
      <span className="faStar fs3">✦</span>
      <span className="faDot fd1" />
      <span className="faDot fd2" />
      <span className="faDot fd3" />
      <span className="faDot fd4" />
    </div>
  );
}

function Sparkles() {
  return (
    <div className="premSparkles" aria-hidden="true">
      {Array.from({ length: 10 }).map((_, i) => (
        <span key={i} className={`premSparkle ps${i + 1}`}>✦</span>
      ))}
    </div>
  );
}

function useSwipeDown(onClose) {
  const shellRef = useRef(null);
  const overlayRef = useRef(null);
  const startY = useRef(0);
  const lastY = useRef(0);
  const startTime = useRef(0);
  const dragStarted = useRef(false);
  const active = useRef(false);
  const pointerId = useRef(null);
  const closeTimer = useRef(null);

  const resetInlineStyles = useCallback(() => {
    const shell = shellRef.current;
    const overlay = overlayRef.current;
    if (!shell || !overlay) return;
    shell.style.animation = "";
    shell.style.transition = "";
    shell.style.transform = "";
    shell.style.opacity = "";
    overlay.style.transition = "";
    overlay.style.opacity = "";
  }, []);

  const finishClose = useCallback(() => {
    const shell = shellRef.current;
    const overlay = overlayRef.current;
    if (!shell || !overlay) { onClose?.(); return; }
    shell.style.transition = "transform 0.26s cubic-bezier(0.32, 0, 0.67, 0), opacity 0.22s ease";
    shell.style.transform = "translate3d(0, 110vh, 0)";
    shell.style.opacity = "0";
    overlay.style.transition = "opacity 0.24s ease";
    overlay.style.opacity = "0";
    window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => {
      onClose?.();
      window.requestAnimationFrame(resetInlineStyles);
    }, 240);
  }, [onClose, resetInlineStyles]);

  useEffect(() => {
    const shell = shellRef.current;
    const overlay = overlayRef.current;
    if (!shell || !overlay) return;

    const getPointY = (event) => {
      if (event.touches?.[0]) return event.touches[0].clientY;
      if (event.changedTouches?.[0]) return event.changedTouches[0].clientY;
      return event.clientY;
    };

    const canStartFromTarget = (event) =>
      Boolean(event.target?.closest?.(".premSwipeHandle"));

    const beginDrag = (event) => {
      if (!canStartFromTarget(event)) return;
      active.current = true;
      pointerId.current = event.pointerId ?? null;
      startY.current = getPointY(event);
      lastY.current = startY.current;
      startTime.current = Date.now();
      dragStarted.current = false;
      if (event.pointerId != null && shell.setPointerCapture) {
        try { shell.setPointerCapture(event.pointerId); } catch {}
      }
    };

    const moveDrag = (event) => {
      if (!active.current) return;
      if (pointerId.current != null && event.pointerId != null && event.pointerId !== pointerId.current) return;
      const currentY = getPointY(event);
      const rawDelta = currentY - startY.current;
      const delta = Math.max(0, rawDelta);
      lastY.current = currentY;
      if (!dragStarted.current) {
        if (delta < 14) return;
        dragStarted.current = true;
        shell.style.animation = "none";
        shell.style.transition = "none";
        shell.style.willChange = "transform, opacity";
        overlay.style.transition = "none";
        overlay.style.willChange = "opacity";
      }
      event.preventDefault?.();
      const progress = Math.min(delta / (window.innerHeight * 0.50), 1);
      shell.style.transform = `translate3d(0, ${delta}px, 0) scale(${1 - progress * 0.025})`;
      shell.style.opacity = String(Math.max(0.34, 1 - progress * 0.66));
      overlay.style.opacity = String(Math.max(0, 1 - progress * 1.05));
    };

    const endDrag = (event) => {
      if (!active.current) return;
      if (pointerId.current != null && event.pointerId != null && event.pointerId !== pointerId.current) return;
      active.current = false;
      if (event.pointerId != null && shell.releasePointerCapture) {
        try { shell.releasePointerCapture(event.pointerId); } catch {}
      }
      if (!dragStarted.current) return;
      dragStarted.current = false;
      const delta = Math.max(0, lastY.current - startY.current);
      const elapsed = Math.max(Date.now() - startTime.current, 1);
      const velocity = delta / elapsed;
      const shouldClose = delta > Math.min(window.innerHeight * 0.34, 285) || (delta > 120 && velocity > 0.85);
      if (shouldClose) { finishClose(); return; }
      shell.style.transition = "transform 0.42s cubic-bezier(0.34, 1.42, 0.64, 1), opacity 0.28s ease";
      shell.style.transform = "translate3d(0, 0, 0) scale(1)";
      shell.style.opacity = "1";
      overlay.style.transition = "opacity 0.28s ease";
      overlay.style.opacity = "1";
      window.setTimeout(() => { if (!active.current) resetInlineStyles(); }, 430);
    };

    const cancelDrag = () => {
      if (!active.current) return;
      active.current = false;
      dragStarted.current = false;
      shell.style.transition = "transform 0.30s ease, opacity 0.24s ease";
      shell.style.transform = "translate3d(0, 0, 0) scale(1)";
      shell.style.opacity = "1";
      overlay.style.transition = "opacity 0.24s ease";
      overlay.style.opacity = "1";
      window.setTimeout(resetInlineStyles, 310);
    };

    shell.addEventListener("pointerdown", beginDrag, { passive: true, capture: true });
    shell.addEventListener("pointermove", moveDrag, { passive: false, capture: true });
    shell.addEventListener("pointerup", endDrag, { passive: true, capture: true });
    shell.addEventListener("pointercancel", cancelDrag, { passive: true, capture: true });

    return () => {
      window.clearTimeout(closeTimer.current);
      shell.removeEventListener("pointerdown", beginDrag, { capture: true });
      shell.removeEventListener("pointermove", moveDrag, { capture: true });
      shell.removeEventListener("pointerup", endDrag, { capture: true });
      shell.removeEventListener("pointercancel", cancelDrag, { capture: true });
    };
  }, [finishClose, resetInlineStyles]);

  return { shellRef, overlayRef };
}

export default function PremiumPopup({
  visible,
  onClose,
  onPurchase,
  onRestore,
  iapEnabled = false,
  mode = "locked",
  featureDesc = "",
  limitLabel = "",
  limitCurrent = 0,
  limitMax = 0,
  benefits,
  plusBenefits,
}) {
  const { t } = useLanguage();
  const [activePlan, setActivePlan] = useState("premium");
  const [restoring, setRestoring]   = useState(false);
  const [notice, setNotice]         = useState("");

  const isPlus  = activePlan === "plus";
  const isLimit = mode === "limit";

  const { shellRef, overlayRef } = useSwipeDown(onClose);

  const PREMIUM_FEATURES = (t("premium.features.premium") !== "premium.features.premium"
    ? t("premium.features.premium") : []).map((f, i) => ({ iconType: PREMIUM_ICON_TYPES[i] || "star", ...f }));

  const PLUS_FEATURES = (t("premium.features.plus") !== "premium.features.plus"
    ? t("premium.features.plus") : []).map((f, i) => ({ iconType: PLUS_ICON_TYPES[i] || "star", ...f }));

  const premiumFeatures = useMemo(() => {
    if (!benefits?.length) return PREMIUM_FEATURES;
    return benefits.slice(0, 5).map((b, i) => ({
      iconType: PREMIUM_FEATURES[i]?.iconType || "star",
      title: b,
      desc: PREMIUM_FEATURES[i]?.desc || "",
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [benefits, activePlan]);

  const plusFeatures = useMemo(() => {
    if (!plusBenefits?.length) return PLUS_FEATURES;
    return plusBenefits.slice(0, 5).map((b, i) => ({
      iconType: PLUS_ICON_TYPES[i] || "star",
      title: typeof b === "string" ? b : b.title,
      desc: typeof b === "object" ? b.desc || "" : PLUS_FEATURES[i]?.desc || "",
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plusBenefits, activePlan]);

  const actualFeatureDesc = featureDesc || t("premium.plan.sub.default");

  const plan = isPlus
    ? { name: t("premium.plan.plus"), price: t("premium.plan.price.plus"), cta: t("premium.plan.cta.plus"),
        sub: t("premium.plan.sub.plus"), features: plusFeatures }
    : { name: t("premium.plan.premium"), price: t("premium.plan.price.premium"), cta: t("premium.plan.cta.premium"),
        sub: isLimit ? actualFeatureDesc : t("premium.plan.sub.default"),
        features: premiumFeatures };

  useEffect(() => {
    if (visible) {
      setActivePlan("premium");
      setNotice("");
      document.body.classList.add("premium-modal-open");
    } else {
      document.body.classList.remove("premium-modal-open");
    }
    return () => document.body.classList.remove("premium-modal-open");
  }, [visible]);

  if (!visible) return null;

  const handlePurchase = () => {
    if (!iapEnabled) {
      // Henüz gerçek satın alma altyapısı bağlı değil — sahte "işlem başarılı"
      // hissi vermek yerine kullanıcıya dürüstçe bildiriyoruz. Buton zaten
      // disabled olduğu için normal şartlarda buraya hiç düşmemeli.
      setNotice(t("premium.comingSoonNotice"));
      return;
    }
    if (typeof onPurchase === "function") onPurchase(isPlus ? "plus" : "user");
  };

  const handleRestore = async () => {
    if (!onRestore || restoring) return;
    setRestoring(true);
    try {
      const ok = await onRestore();
      if (ok) onClose?.();
      else setNotice(t("premium.notFound"));
    } catch {
      setNotice(t("premium.restoreFailed"));
    } finally {
      setRestoring(false);
    }
  };

  return (
    <div
      ref={overlayRef}
      className="premOverlay"
      role="dialog"
      aria-modal="true"
      aria-label="Premium popup"
      onClick={() => onClose?.()}
    >
      <section
        ref={shellRef}
        className="premShell"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="premSwipeHandle">
          <div className="premSwipeBar" />
        </div>

        <Sparkles />

        <button type="button" className="premCloseBtn" onClick={() => onClose?.()} aria-label={t("premium.closeLabel")}>
          ×
        </button>

        {/* ── SCROLL BODY: hero + tabs + card içeriği ── */}
        <div className="premScrollBody">
          <header className="premHero">
            <div className="premCrownWrap">
              <GlowDots />
              <img src={CROWN_IMG} alt={t("premium.crownAlt")} className="premCrownImg" draggable={false} />
            </div>
            <h2 className="premTitle">
              {t("premium.hero.title1")}{" "}
              <span className="premTitleAccent">{t("premium.hero.title2")}</span>{" "}
              <span className="premTitleMain">{t("premium.hero.title3")}</span>
            </h2>
            <p className="premSubtitle">
              {t("premium.hero.subtitle").split("\n").map((line, i) => (
                <span key={i}>{line}{i === 0 && <br />}</span>
              ))}
            </p>
          </header>

          <div className="premTabs" role="tablist">
            <button
              type="button" role="tab" aria-selected={!isPlus}
              className={`premTab${!isPlus ? " active" : ""}`}
              onClick={() => setActivePlan("premium")}
            >
              <span className="premTabIconWrap">
                <img src={CROWN_IMG} alt="" className="premTabIcon" />
              </span>
              Premium
            </button>
            <button
              type="button" role="tab" aria-selected={isPlus}
              className={`premTab${isPlus ? " active plus" : ""}`}
              onClick={() => setActivePlan("plus")}
            >
              <span className="premTabIconWrap plus">
                <img src={DIAMOND_IMG} alt="" className="premTabIcon" />
              </span>
              Premium Plus
            </button>
          </div>

          <div className={`premCard${isPlus ? " plusCard" : ""}`}>
            <div className="premCardTop">
              <div className="premPriceBlock">
                <span className="premPlanLabel">{plan.name}</span>
                <div className="premPriceRow">
                  <strong className="premPriceNum">{plan.price}</strong>
                  <span className="premPricePer">{t("premium.perMonth")}</span>
                </div>
                <p className="premPriceSub">{plan.sub}</p>
              </div>
              <div className="premFamilyWrap">
                <FamilyAura />
                <img src={FAMILY_IMG} alt={t("premium.familyAlt")} className="premFamilyImg" draggable={false} />
              </div>
            </div>

            {isLimit && (
              <div className="premLimitBox">
                <div className="premLimitRow">
                  <span>{limitLabel}</span>
                  <strong>{limitCurrent} / {limitMax}</strong>
                </div>
                <div className="premLimitTrack">
                  <i style={{ width: `${Math.min((limitCurrent / limitMax) * 100, 100)}%` }} />
                </div>
              </div>
            )}

            {/* Feature list: max 2 satır görünür, kaydırılabilir */}
            <div className="premFeatureList">
              {plan.features.map((item, idx) => (
                <div className="premFeatureRow" key={`${activePlan}-${idx}`}>
                  <FeatureIcon type={item.iconType} isPlus={isPlus} />
                  <div className="premFeatureText">
                    <strong>{item.title}</strong>
                    <small>{item.desc}</small>
                  </div>
                  <CheckIcon />
                </div>
              ))}
            </div>

            {notice && <p className="premNotice">{notice}</p>}
          </div>
        </div>

        {/* ── SABİT ALT KISIM: CTA her zaman görünür ── */}
        <div className="premScrollFoot">
          <button
            type="button"
            className={`premCTA${isPlus ? " plusCTA" : ""}${!iapEnabled ? " premCTADisabled" : ""}`}
            onClick={handlePurchase}
            disabled={!iapEnabled}
            aria-disabled={!iapEnabled}
          >
            <img src={isPlus ? DIAMOND_IMG : CROWN_IMG} alt="" className="premCTAIcon ctaIconBalance" aria-hidden="true" />
            <div className="premCTAText">
              <b>{iapEnabled ? plan.cta : t("premium.comingSoon")}</b>
              {iapEnabled && <small>{plan.price} {t("premium.perMonth")}</small>}
            </div>
            <img src={isPlus ? DIAMOND_IMG : CROWN_IMG} alt="" className="premCTAIcon ctaIconVisible" />
          </button>

          {iapEnabled ? (
            <div className="premSafeRow">
              <svg viewBox="0 0 24 24" fill="none" width="12" height="12">
                <rect x="3" y="11" width="18" height="11" rx="2" stroke="currentColor" strokeWidth="2"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              {t("premium.safe")}
              <span className="premDot" />
              {t("premium.cancel")}
            </div>
          ) : (
            <p className="premComingSoonSub">{t("premium.comingSoonSub")}</p>
          )}

          <button type="button" className="premLaterBtn" onClick={() => onClose?.()}>
            {t("premium.later")}
          </button>
          {iapEnabled && onRestore && (
            <button type="button" className="premRestoreBtn" onClick={handleRestore} disabled={restoring}>
              {restoring ? t("premium.restoring") : t("premium.restoreBtn")}
            </button>
          )}
        </div>

      </section>
    </div>
  );
}